/**
 * Arc and path layer configuration for globe.gl 3D globe.
 *
 * Configures trade route arcs, submarine cables, pipelines, satellite orbits,
 * and storm track paths on the globe instance with status-driven color coding.
 */

import type { GlobeInstance } from 'globe.gl';

// ── Arc Types ───────────────────────────────────────────────────────

export interface GlobeArc {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  color?: string[];
  dashLength?: number;
  dashGap?: number;
  dashAnimateTime?: number;
  label?: string;
  status?: string;
  category?: string;
}

// ── Path Types ──────────────────────────────────────────────────────

export interface GlobePath {
  points: Array<{ lat: number; lng: number; alt?: number }>;
  color?: string;
  width?: number;
  pathType?: 'cable' | 'pipeline-oil' | 'pipeline-gas' | 'orbit' | 'storm-track';
  status?: string;
}

// ── Arc Color Constants ─────────────────────────────────────────────

const ARC_COLORS = {
  disrupted: ['rgba(255,80,80,0.2)', 'rgba(255,80,80,0.8)', 'rgba(255,80,80,0.2)'],
  highRisk: ['rgba(255,180,50,0.2)', 'rgba(255,180,50,0.7)', 'rgba(255,180,50,0.2)'],
  energy: ['rgba(255,180,50,0.1)', 'rgba(255,180,50,0.6)', 'rgba(255,180,50,0.1)'],
  container: ['rgba(100,200,255,0.1)', 'rgba(100,200,255,0.6)', 'rgba(100,200,255,0.1)'],
  default: ['rgba(100,200,255,0.1)', 'rgba(100,200,255,0.6)', 'rgba(100,200,255,0.1)'],
} as const;

const DEFAULT_DASH_LENGTH = 0.9;
const DEFAULT_DASH_GAP = 4;
const DEFAULT_DASH_ANIMATE_TIME = 5000;

// ── Path Color Constants ────────────────────────────────────────────

const CABLE_COLORS: Record<string, string> = {
  fault: '#ff3030',
  degraded: '#ff8800',
  normal: 'rgba(0,200,255,0.65)',
};

const PIPELINE_COLORS: Record<string, string> = {
  oil: 'rgba(255,140,0,0.6)',
  gas: 'rgba(80,220,120,0.6)',
};

const ORBIT_COLORS: Record<string, string> = {
  CN: '#ff2020',
  RU: '#ff8800',
  US: '#4488ff',
  EU: '#44cc44',
};

const STORM_TRACK_COLOR = 'rgba(255,100,100,0.8)';

// ── Color Functions ─────────────────────────────────────────────────

/** Determine arc color gradient based on status and category. */
export function getArcColor(arc: GlobeArc): string[] {
  if (arc.color) return arc.color;

  const status = arc.status?.toLowerCase() ?? '';
  const category = arc.category?.toLowerCase() ?? '';

  if (status === 'disrupted') return [...ARC_COLORS.disrupted];
  if (status === 'high-risk' || status === 'high risk') return [...ARC_COLORS.highRisk];
  if (category === 'energy') return [...ARC_COLORS.energy];
  if (category === 'container') return [...ARC_COLORS.container];

  return [...ARC_COLORS.default];
}

/** Determine path color based on type, status, and optional country. */
export function getPathColor(path: GlobePath): string {
  if (path.color) return path.color;

  const pathType = path.pathType;
  const status = path.status?.toLowerCase() ?? '';

  switch (pathType) {
    case 'cable':
      if (status === 'fault') return CABLE_COLORS.fault;
      if (status === 'degraded') return CABLE_COLORS.degraded;
      return CABLE_COLORS.normal;

    case 'pipeline-oil':
      return PIPELINE_COLORS.oil;

    case 'pipeline-gas':
      return PIPELINE_COLORS.gas;

    case 'orbit':
      return ORBIT_COLORS[status.toUpperCase()] ?? ORBIT_COLORS.US;

    case 'storm-track':
      return STORM_TRACK_COLOR;

    default:
      return 'rgba(68,204,136,0.6)';
  }
}

// ── Globe Configuration ─────────────────────────────────────────────

/** Configure arc layers on a globe.gl instance. */
export function configureGlobeArcs(globe: GlobeInstance, arcs: GlobeArc[]): void {
  globe
    .arcsData(arcs)
    .arcStartLat((d: object) => (d as GlobeArc).startLat)
    .arcStartLng((d: object) => (d as GlobeArc).startLng)
    .arcEndLat((d: object) => (d as GlobeArc).endLat)
    .arcEndLng((d: object) => (d as GlobeArc).endLng)
    .arcColor((d: object) => getArcColor(d as GlobeArc))
    .arcDashLength((d: object) => (d as GlobeArc).dashLength ?? DEFAULT_DASH_LENGTH)
    .arcDashGap((d: object) => (d as GlobeArc).dashGap ?? DEFAULT_DASH_GAP)
    .arcDashAnimateTime((d: object) => (d as GlobeArc).dashAnimateTime ?? DEFAULT_DASH_ANIMATE_TIME)
    .arcStroke(0.5)
    .arcLabel((d: object) => (d as GlobeArc).label ?? '');
}

/** Configure path layers on a globe.gl instance. */
export function configureGlobePaths(globe: GlobeInstance, paths: GlobePath[]): void {
  globe
    .pathsData(paths)
    .pathPoints((d: object) => (d as GlobePath).points)
    .pathPointLat((p: object) => (p as { lat: number }).lat)
    .pathPointLng((p: object) => (p as { lng: number }).lng)
    .pathPointAlt((p: object) => (p as { alt?: number }).alt ?? 0)
    .pathColor((d: object) => getPathColor(d as GlobePath))
    .pathStroke((d: object) => (d as GlobePath).width ?? 0.5)
    .pathLabel((d: object) => {
      const path = d as GlobePath;
      return path.pathType ?? '';
    });
}
